import React, { useEffect, useRef } from 'react';

/**
 * BadWordWarning
 *
 * A reusable, animated warning banner shown when inappropriate language
 * is detected in user input.
 *
 * Props:
 *   visible  {boolean}  – controls show/hide (animated)
 *   message  {string}   – optional override for the warning text
 *   style    {object}   – optional extra styles on the wrapper
 *
 * Usage:
 *   <BadWordWarning visible={hasBadWord} />
 */
const BadWordWarning = ({
  visible,
  message,
  style = {},
}) => {
  const wrapperRef = useRef(null);

  // Shake the banner once each time it newly becomes visible
  useEffect(() => {
    if (!visible || !wrapperRef.current) return;
    const el = wrapperRef.current;
    el.classList.remove('bww-shake');
    // Trigger reflow so the animation restarts cleanly
    void el.offsetWidth;
    el.classList.add('bww-shake');
  }, [visible]);

  const defaultMessage =
    message || "⚠️ Your message contains inappropriate language. Please keep it respectful.";

  return (
    <>
      {/* Scoped styles — no external CSS file needed */}
      <style>{`
        .bww-outer {
          overflow: hidden;
          max-height: 0;
          opacity: 0;
          transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.25s ease,
                      margin 0.35s ease;
          margin-top: 0;
          margin-bottom: 0;
        }

        .bww-outer.bww-visible {
          max-height: 120px;
          opacity: 1;
          margin-top: 8px;
          margin-bottom: 4px;
        }

        .bww-banner {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin: 0px 10px;
          padding: 10px 14px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.23);
          border: 1.5px solid rgba(255, 255, 255, 0.35);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .bww-icon {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          margin-top: 1px;
        }

        .bww-text {
          font-size: 14px;
          color: #fff;
          padding: 3px 0 0 0;
          letter-spacing: 0.01em;
        }

        @keyframes bww-shake-kf {
          0%   { transform: translateX(0); }
          15%  { transform: translateX(-5px); }
          30%  { transform: translateX(5px); }
          45%  { transform: translateX(-4px); }
          60%  { transform: translateX(4px); }
          75%  { transform: translateX(-2px); }
          90%  { transform: translateX(2px); }
          100% { transform: translateX(0); }
        }

        .bww-shake {
          animation: bww-shake-kf 0.5s ease-in-out;
        }
      `}</style>

      <div
        className={`GTRegular bww-outer${visible ? ' bww-visible' : ''}`}
        style={style}
        aria-live="polite"
        aria-atomic="true"
      >
        <div ref={wrapperRef} className="bww-banner">
          {/* Warning icon */}
          <svg
            className="bww-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              fill="rgba(255,59,48,0.2)"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              x1="12" y1="9" x2="12" y2="13"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <circle cx="12" cy="17" r="0.8" fill="#fff" stroke="#fff" strokeWidth="1" />
          </svg>

          <p className="bww-text m-0">{defaultMessage}</p>
        </div>
      </div>
    </>
  );
};

export default BadWordWarning;